import React, { useEffect, useState } from 'react'
import { Drawer } from '@mui/material'
import { RenderWhenVisible } from '@marketing-milk/frontend'
import { AdCreative, AssetApprovalStatus, LeadCard } from '@marketing-milk/interfaces'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'
import { CreativeListRow } from 'pages/business/creatives/CreativeCard/CreativeListRow'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'

type Props = {
  creatives: AdCreative[]
  declaredForms: LeadCard[]
  selectedIds: Set<number>
  selectCreative: (creative: AdCreative) => void
  setSelected: (ads: AdCreative[], selected: boolean) => void
  onPreview: (form: LeadCard) => void
}

export const isPublished = (creative: AdCreative) =>
  creative.reviewStatus === AssetApprovalStatus.Published

// One section per form makes assignments visible as rows landing in their new section.
export const AdsByLeadForm = ({
  creatives,
  declaredForms,
  selectedIds,
  selectCreative,
  setSelected,
  onPreview,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [previewCreative, setPreviewCreative] = useState<AdCreative>()
  const visible = searchTerm ? fuzzySearch(creatives, searchTerm) : creatives
  const [collapsed, setCollapsed] = useState<Set<string | number>>(new Set())
  const selectableAds = visible.filter(c => !isPublished(c))
  const allVisibleSelected =
    selectableAds.length > 0 && selectableAds.every(c => selectedIds.has(c.id))
  const hiddenSelected = creatives.filter(
    c => selectedIds.has(c.id) && !visible.some(v => v.id === c.id)
  ).length

  const sectionActionClass =
    'whitespace-nowrap rounded-md px-2.5 py-1 text-sm font-semibold text-mm-primary-700 hover:bg-mm-primary-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'

  const renderSection = (
    key: string | number,
    title: string,
    inSection: (c: AdCreative) => boolean,
    form?: LeadCard
  ) => {
    const isCollapsed = collapsed.has(key)
    const total = creatives.filter(inSection).length
    const rows = visible.filter(inSection)
    const selectable = rows.filter(c => !isPublished(c))
    const allSelected = selectable.length > 0 && selectable.every(c => selectedIds.has(c.id))

    return (
      <section
        key={key}
        className="overflow-hidden rounded-lg border border-mm-neutral-1-200 bg-white"
      >
        <header className="relative flex flex-wrap items-center gap-2 border-b border-mm-neutral-1-200 bg-mm-neutral-2-50 px-4 py-2 hover:bg-mm-neutral-1-100">
          <h4 className="min-w-0 flex-1 text-sm font-semibold text-mm-neutral-1-900">
            <button
              type="button"
              aria-expanded={!isCollapsed}
              className="flex items-center gap-2 text-left"
              onClick={() =>
                setCollapsed(previous => {
                  const next = new Set(previous)
                  if (next.has(key)) next.delete(key)
                  else next.add(key)
                  return next
                })
              }
            >
              {/* Extend the native button's hit area across the bar; actions sit above it. */}
              <span aria-hidden="true" className="absolute inset-0 cursor-pointer" />
              <span aria-hidden="true">{isCollapsed ? '▸' : '▾'}</span>
              <span className="break-words">
                {title}{' '}
                <span className="font-normal text-mm-neutral-1-500">
                  · {searchTerm ? `${rows.length} of ${total}` : total}
                </span>
              </span>
            </button>
          </h4>
          {form && (
            <button
              type="button"
              className={`${sectionActionClass} relative z-10`}
              onClick={() => onPreview(form)}
            >
              Preview form
            </button>
          )}
          {selectable.some(c => selectedIds.has(c.id)) && (
            <span className="text-xs text-mm-primary-700">
              {selectable.filter(c => selectedIds.has(c.id)).length} selected
            </span>
          )}
          <button
            type="button"
            className={`${sectionActionClass} relative z-10`}
            disabled={selectable.length === 0}
            onClick={() => setSelected(selectable, !allSelected)}
          >
            {allSelected ? 'Deselect' : 'Select'} {searchTerm ? 'matching' : 'all'}
          </button>
        </header>
        {!isCollapsed &&
          (rows.length > 0 ? (
            <div className="divide-y divide-mm-neutral-1-100">
              {rows.map(creative => (
                <RenderWhenVisible key={creative.id}>
                  <CreativeListRow
                    creative={creative}
                    onSelect={selectCreative}
                    selected={selectedIds.has(creative.id)}
                    disabled={isPublished(creative)}
                    showLeadForm={false}
                    onPreview={setPreviewCreative}
                  />
                </RenderWhenVisible>
              ))}
            </div>
          ) : (
            <p className="px-4 py-3 text-sm text-mm-neutral-1-500">
              {/* Only "No form yet" can be empty: a form's section exists because an ad uses it. */}
              {total > 0 ? 'None of these match your filter.' : 'Every ad has a form.'}
            </p>
          ))}
      </section>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-mm-primary-200 bg-mm-primary-50 p-4">
        <h3 className="text-lg font-semibold text-mm-neutral-1-900">
          Select ads, then assign a form
        </h3>
        <p className="mt-1 text-sm text-mm-neutral-1-700">
          Choose individual ads or a whole group. Use “Assign to form” below to pick an existing
          form or create a new one.
        </p>
        <button
          type="button"
          className={`${sectionActionClass} mt-3 border border-mm-primary-300 bg-white`}
          disabled={selectableAds.length === 0}
          onClick={() => setSelected(selectableAds, !allVisibleSelected)}
        >
          {allVisibleSelected ? 'Deselect' : 'Select'} all {searchTerm ? 'matching ' : ''}ads (
          {selectableAds.length})
        </button>
        <p className="mt-2 text-xs text-mm-neutral-1-600">
          Changes save immediately and apply wherever these ads are used. Published ads are locked.
        </p>
      </div>
      <div className="relative">
        <input
          className="w-full rounded-md border border-mm-neutral-1-200 bg-white py-2.5 pl-4 pr-20 text-sm"
          type="text"
          aria-label="Filter ads"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Filter ads by name or copy"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 px-4 text-sm font-semibold text-mm-primary-700 hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {hiddenSelected > 0 && (
        <p role="status" className="text-sm text-mm-primary-700">
          {hiddenSelected} selected {hiddenSelected === 1 ? 'ad is' : 'ads are'} hidden by this
          filter. Assigning will include {hiddenSelected === 1 ? 'it' : 'them'}.
        </p>
      )}
      {renderSection('none', 'No form yet', c => !c.leadCardUsed)}

      {declaredForms.map(form =>
        renderSection(form.id, form.formName, c => c.leadCardUsed?.id === form.id, form)
      )}
      {previewCreative && (
        <AdPreview
          key={previewCreative.id}
          creative={previewCreative}
          onClose={() => setPreviewCreative(undefined)}
        />
      )}
    </div>
  )
}

const AdPreview = ({ creative, onClose }: { creative: AdCreative; onClose: () => void }) => {
  const { adCreativePreview, retrievePreview, loadingPreview, refreshing } =
    useFetchCreativePreview(creative.id, undefined, { suppressErrorToast: true })

  useEffect(() => {
    void retrievePreview()
  }, [retrievePreview])

  return (
    <Drawer anchor="right" open onClose={onClose}>
      <div className="flex h-full w-screen max-w-lg flex-col p-4">
        <div className="mb-4 flex items-center gap-4">
          <h3 className="min-w-0 flex-1 break-words text-base font-semibold">{creative.name}</h3>
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold">
            Close ad preview
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CreativePreview
            creative={creative}
            iframeSource={getIFrameSource(adCreativePreview ?? '')}
            loading={loadingPreview}
            refreshing={refreshing}
            onRetry={() => retrievePreview()}
          />
        </div>
      </div>
    </Drawer>
  )
}
